import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ArrowLeft, Search, Plus, Activity, Heart, Dumbbell, Bike } from 'lucide-react';
import { exerciseDatabase } from '../mock/mockData';
import { useToast } from '../hooks/use-toast';

const typeIcons = {
  cardio: Heart,
  strength: Dumbbell,
  flexibility: Activity,
};

const AddExercise = () => {
  const navigate = useNavigate();
  const { addExercise } = useApp();
  const { toast } = useToast();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [minutes, setMinutes] = useState(30);

  const results = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return exerciseDatabase;
    return exerciseDatabase.filter((e) => e.name.toLowerCase().includes(t));
  }, [q]);

  const handleSave = () => {
    if (!selected) return;
    const cal = Math.round(selected.caloriesPerMin * minutes);
    addExercise({
      name: selected.name,
      minutes,
      calories: cal,
      time: new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
    });
    toast({ title: 'Exercise logged', description: `${selected.name} for ${minutes} min (${cal} cal)` });
    navigate('/diary');
  };

  return (
    <div className="flex flex-col bg-white min-h-screen">
      <header className="bg-emerald-600 text-white px-4 pt-5 pb-3 sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-full hover:bg-white/10" aria-label="Back">
            <ArrowLeft size={22} />
          </button>
          <h1 className="text-lg font-bold flex-1">Add Exercise</h1>
        </div>
        <div className="mt-3 relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search exercises"
            className="w-full bg-white text-gray-900 rounded-xl pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-white/40"
          />
        </div>
      </header>

      {!selected ? (
        <ul className="divide-y divide-gray-100">
          {results.map((e) => {
            const Icon = typeIcons[e.type] || Activity;
            return (
              <li key={e.id}>
                <button
                  onClick={() => setSelected(e)}
                  className="w-full px-4 py-3.5 flex items-center gap-3 hover:bg-gray-50 text-left press-scale"
                >
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <Icon size={18} className="text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">{e.name}</p>
                    <p className="text-xs text-gray-500">{Math.round(e.caloriesPerMin)} cal/min · {e.type}</p>
                  </div>
                  <Plus size={18} className="text-emerald-600" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="p-5 flex flex-col gap-5">
          <div className="bg-gray-50 rounded-2xl p-4">
            <p className="text-xs uppercase tracking-wider text-gray-500">Selected</p>
            <p className="text-base font-bold text-gray-900 mt-1">{selected.name}</p>
          </div>

          <div>
            <label className="text-sm font-semibold text-gray-900">Duration</label>
            <div className="mt-2 flex items-center gap-3">
              <button
                onClick={() => setMinutes((m) => Math.max(5, m - 5))}
                className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 font-bold text-gray-700"
              >−</button>
              <div className="flex-1 text-center">
                <p className="text-3xl font-bold text-gray-900">{minutes}</p>
                <p className="text-xs text-gray-500">minutes</p>
              </div>
              <button
                onClick={() => setMinutes((m) => m + 5)}
                className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 font-bold text-gray-700"
              >+</button>
            </div>
            <input
              type="range"
              min={5}
              max={120}
              step={5}
              value={minutes}
              onChange={(e) => setMinutes(parseInt(e.target.value, 10))}
              className="w-full mt-4 accent-emerald-600"
            />
          </div>

          <div className="bg-emerald-50 rounded-2xl p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-emerald-700">Calories burned</p>
              <p className="text-2xl font-bold text-emerald-700">
                {Math.round(selected.caloriesPerMin * minutes)}
              </p>
            </div>
            <Bike size={36} className="text-emerald-500" />
          </div>

          <div className="flex gap-3 mt-2">
            <button onClick={() => setSelected(null)} className="flex-1 py-3 rounded-xl border border-gray-200 font-semibold text-gray-700 press-scale">
              Back
            </button>
            <button onClick={handleSave} className="flex-1 py-3 rounded-xl bg-emerald-600 text-white font-bold press-scale hover:bg-emerald-700">
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddExercise;
